import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ImageOff, Pencil, Plus } from 'lucide-react';
import { getAllCategories, getAllProducts, getCategory } from '../../data/catalog';
import { PHOTO_STATUS, PHOTO_STATUS_LABEL, getPhotoStatus, usePhotoTick } from '../../utils/photoStatus';
import { isVariantless } from '../../utils/availability';

const PHOTO_FILTERS = [
  { key: 'todas', label: 'Todos' },
  { key: PHOTO_STATUS.APROVADA, label: 'Fotos aprovadas' },
  { key: PHOTO_STATUS.PENDENTE, label: 'Fotos pendentes' },
  { key: PHOTO_STATUS.REVISAO, label: 'Fotos em revisão' },
];

export default function AdminProducts() {
  usePhotoTick();
  const [photoFilter, setPhotoFilter] = useState('todas');
  // Filtro de categoria na URL (?cat=slug): preservado no fluxo Produtos → Editar → Voltar.
  const [searchParams, setSearchParams] = useSearchParams();
  const catFilter = searchParams.get('cat') || '';
  // Inclui TODAS as categorias no filtro, mesmo as ainda sem produtos.
  const categories = [...getAllCategories()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  const products = getAllProducts().map((p) => {
    const counts = p.variants.reduce((acc, v) => {
      const s = getPhotoStatus(v);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    return { p, counts };
  });
  const shown = products.filter(
    ({ p, counts }) =>
      (catFilter === '' || p.categorySlug === catFilter) &&
      (photoFilter === 'todas' || counts[photoFilter] > 0)
  );

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{getAllProducts().length} produtos cadastrados (demonstração + cadastrados pelo painel)</p>
        <Link
          to="/admin/produtos/novo"
          data-testid="admin-new-product-button"
          className="flex shrink-0 items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
        >
          <Plus className="h-4 w-4" /> NOVO PRODUTO
        </Link>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label htmlFor="admin-category-filter" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Categoria
        </label>
        <select
          id="admin-category-filter"
          data-testid="admin-category-filter"
          value={catFilter}
          onChange={(e) => setSearchParams(e.target.value ? { cat: e.target.value } : {})}
          className="h-11 min-w-0 flex-1 rounded-xl border border-input bg-white px-3 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15 sm:max-w-xs"
        >
          <option value="">Todas as categorias</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>{c.name}</option>
          ))}
        </select>
        {catFilter && (
          <button
            type="button"
            onClick={() => setSearchParams({})}
            data-testid="admin-category-filter-clear"
            className="h-11 rounded-xl border border-navy/20 px-4 text-xs font-semibold text-navy hover:bg-navy-light"
          >
            LIMPAR
          </button>
        )}
      </div>

      <div className="no-scrollbar -mx-4 mt-4 flex gap-2 overflow-x-auto px-4" data-testid="admin-products-photo-filters">
        {PHOTO_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setPhotoFilter(f.key)}
            aria-pressed={photoFilter === f.key}
            data-testid={`admin-photo-filter-${f.key === 'todas' ? 'todas' : f.key.toLowerCase()}`}
            className={`shrink-0 rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
              photoFilter === f.key ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {shown.map(({ p, counts }) => {
          const pending = (counts[PHOTO_STATUS.PENDENTE] || 0) + (counts[PHOTO_STATUS.REVISAO] || 0);
          const variantless = isVariantless(p);
          const thumb = p.coverImage || p.variants[0]?.image || p.image || null;
          return (
            <div key={p.slug} className="overflow-hidden rounded-xl border border-navy/10 bg-white" data-testid={`admin-product-card-${p.slug}`}>
              {thumb ? (
                <div className="relative">
                  <img src={thumb} alt={p.name} loading="lazy" className="aspect-square w-full object-cover" />
                  {p.coverImage && thumb === p.coverImage && (
                    <span
                      className="absolute bottom-1.5 left-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-white/90"
                      data-testid={`admin-product-illustrative-${p.slug}`}
                    >
                      Imagem ilustrativa
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex aspect-square w-full items-center justify-center bg-navy-light">
                  <ImageOff className="h-6 w-6 text-navy/40" />
                </div>
              )}
              <div className="p-3">
                <p className="text-sm font-medium leading-tight">{p.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`admin-product-meta-${p.slug}`}>
                  {variantless
                    ? 'Sem variantes (produto único)'
                    : p.variants.length === 0
                      ? 'Produto mãe — aguardando variantes'
                      : `${p.variants.length} ${p.variants.length === 1 ? 'variante' : 'variantes'}`}{' '}
                  · {getCategory(p.categorySlug)?.name}
                </p>
                {pending > 0 && (
                  <p className="mt-1.5 inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`admin-product-photo-badge-${p.slug}`}>
                    {counts[PHOTO_STATUS.PENDENTE] ? `${counts[PHOTO_STATUS.PENDENTE]} ${PHOTO_STATUS_LABEL[PHOTO_STATUS.PENDENTE].toLowerCase()}` : ''}
                    {counts[PHOTO_STATUS.PENDENTE] && counts[PHOTO_STATUS.REVISAO] ? ' · ' : ''}
                    {counts[PHOTO_STATUS.REVISAO] ? `${counts[PHOTO_STATUS.REVISAO]} em revisão` : ''}
                  </p>
                )}
                <Link
                  to={`/admin/produtos/editar/${p.slug}${catFilter ? `?cat=${encodeURIComponent(catFilter)}` : ''}`}
                  data-testid={`admin-product-edit-${p.slug}`}
                  className="mt-2 flex h-10 w-full items-center justify-center gap-1.5 rounded-full border border-navy/25 text-xs font-semibold text-navy transition-colors hover:bg-navy-light"
                >
                  <Pencil className="h-3.5 w-3.5" /> EDITAR
                </Link>
              </div>
            </div>
          );
        })}
      </div>
      {shown.length === 0 && (
        <p className="mt-5 rounded-2xl border border-dashed border-navy/20 p-8 text-center text-sm text-muted-foreground" data-testid="admin-products-empty">
          Nenhum produto neste filtro.
        </p>
      )}
    </div>
  );
}
